import * as yup from 'yup';
import {
  arrivalTimes,
  classLevels,
  departureTimes,
  modesOfTransport,
  NUMBER_OF_TOUR_STUDENTS,
  otherGroupsFormEstablishmentTypes,
  schoolFormEstablishmentTypes,
  timeSlots,
  tourTypes,
  visitDays,
} from './data';

const schoolFormEstablishmentDetailsSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([...schoolFormEstablishmentTypes.map((type) => type.value)])
    .required('type'),
  postalCode: yup.string().required('postalCode'),
  name: yup.string().required('name'),
  city: yup.string().required('city'),
  address: yup.string().required('address'),
  department: yup.string().required('department'),
  privateContract: yup.boolean().required('privateContract'),
  rep: yup.boolean().required('rep'),
});

const inchargePersonSchema = yup.object().shape({
  firstName: yup.string().required('firstName'),
  lastName: yup.string().required('lastName'),
  function: yup.string().required('function'),
  email: yup.string().email('wrongEmailFormat').required('email'),
  phone: yup.string().required('phone'),
});

// Reusable test function for timeSlot student range validation
const checkTourStudentsRangeTestFn = function (value) {
  const currentPath = this.path; // e.g., "classes[0].experienceDetails.timeSlot"
  const pathParts = currentPath.split('.'); // Splits into ["classes[0]", "experienceDetails", "timeSlot"]

  let classItemIndexStr = null;
  const classesPart = pathParts.find((part) => part.startsWith('classes['));
  if (classesPart) {
    // Extract index from "classes[0]" -> "0"
    classItemIndexStr = classesPart.substring(
      classesPart.indexOf('[') + 1,
      classesPart.indexOf(']'),
    );
  }

  if (!classItemIndexStr) {
    // Cannot determine class index from path, skip this validation.
    return true;
  }

  const formValues = this.options.context;
  const currentClassValues = formValues?.classes?.[classItemIndexStr];

  if (!currentClassValues) {
    return true; // Class data not found
  }

  const numberOfStudentsValue = currentClassValues.numberOfStudents;
  const tourTypeValue = currentClassValues.experienceDetails?.tourType;

  if (!tourTypeValue || !NUMBER_OF_TOUR_STUDENTS[tourTypeValue]) {
    // If tourType is not selected or no limits defined for it, this specific validation isn't applicable.
    // Other validations (e.g., tourType being required) will handle an unselected/invalid tourType.
    return true;
  }

  const limits = NUMBER_OF_TOUR_STUDENTS[tourTypeValue];
  const numStudents = parseInt(numberOfStudentsValue, 10);

  if (isNaN(numStudents)) return true; // Skip validation if not a number

  if (numStudents < limits.MIN || numStudents > limits.MAX) {
    return this.createError({
      path: currentPath,
      message:
        numStudents < limits.MIN
          ? 'numberOfStudentsTooSmallForTour'
          : 'numberOfStudentsTooLargeForTour',
    });
  }

  return true;
};

const schoolRegistrationFormClassSchema = yup.object().shape({
  name: yup.string().required('name'),
  level: yup
    .string()
    .oneOf(classLevels.map((level) => level.value))
    .required('level'),
  otherLevel: yup.string().when('level', {
    is: (level) => level === 'other',
    then: () => yup.string().required('otherLevel'),
    otherwise: () => yup.string().notRequired(),
  }),
  numberOfStudents: yup
    .number()
    .min(1, 'numberOfStudents')
    .max(50, 'numberOfStudents')
    .required('numberOfStudents'),
  sector: yup.string(),
  visitDay: yup
    .string()
    .oneOf(visitDays.map((day) => day.value))
    .required('visitDay'),
  arrivalTime: yup
    .string()
    .test('visitDay', 'arrivalTime', (value, testContext) => {
      const { parent } = testContext;
      const { visitDay } = parent;
      return arrivalTimes[visitDay].map((time) => time.value).includes(value);
    })
    .required('arrivalTime'),
  departureTime: yup
    .string()
    .test('visitDay', 'departureTime', (value, testContext) => {
      const { parent } = testContext;
      const { visitDay } = parent;
      return departureTimes[visitDay].map((time) => time.value).includes(value);
    })
    .required('departureTime'),
  modeOfTransport: yup
    .string()
    .oneOf(['', ...modesOfTransport.map((transport) => transport.value)])
    .notRequired(),
  otherModeOfTransport: yup.string().when('modeOfTransport', {
    is: (modeOfTransport) => modeOfTransport === 'other',
    then: () => yup.string().required('otherModeOfTransport'),
    otherwise: () => yup.string().notRequired(),
  }),
  picnicOnSite: yup.boolean().required('picnicOnSite'),
  handicap: yup.boolean().required('handicap'),
  handicapDescription: yup.string().when('handicap', {
    is: (value) => value === true || value === 'true',
    then: () => yup.string().required('handicapDescription'),
    otherwise: () => yup.string().notRequired(),
  }),
  responsibleInformation: yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      function: yup.string(),
      email: yup.string().email('wrongEmailFormat'),
      phone: yup.string(),
    })
    .nullable(),
  bookExperience: yup.boolean().required('bookExperience'),
  experienceDetails: yup.object().when('bookExperience', {
    is: (value) => value === true || value === 'true',
    then: () =>
      yup.object().shape({
        level: yup
          .string()
          .oneOf([...classLevels].map((level) => level.value))
          .required('level'),
        tourType: yup
          .string()
          .oneOf(
            [...tourTypes].map((tour) => tour.value),
            'correctTourType',
          )
          .required('tourType'),
        timeSlot: yup
          .string()
          .when('tourType', {
            is: (tourType) => tourType === 'introduction',
            then: () =>
              yup
                .string()
                .oneOf(
                  timeSlots.introduction.map((slot) => slot.value),
                  'correctTimeSlot',
                )
                .required('timeSlot'),
            // .test({
            //   name: 'checkTourStudentsRange',
            //   message: 'studentsRangeCheck',
            //   test: checkTourStudentsRangeTestFn,
            // }),
            otherwise: () =>
              yup
                .string()
                .oneOf(
                  timeSlots.guided.map((slot) => slot.value),
                  'correctTimeSlot',
                )
                .required('timeSlot'),
            // .test({
            //   name: 'checkTourStudentsRange',
            //   message: 'studentsRangeCheck',
            //   test: checkTourStudentsRangeTestFn,
            // }),
          })
          .required('timeSlot'),
        guidedTourType: yup.string().when('tourType', {
          is: (tourType) => tourType === 'guided',
          then: () => yup.string().required('guidedTourType'),
          otherwise: () => yup.string().notRequired(),
        }),
        professionId: yup.string().when('guidedTourType', {
          is: (guidedTourType) => guidedTourType,
          then: () => yup.string().required('professionId'),
          otherwise: () => yup.string().notRequired(),
        }),
      }),
    otherwise: () => yup.object().nullable(),
  }),
});

const emailConsentSchema = yup.object().shape({
  emailConsent: yup.boolean().required('emailConsent'),
  email: yup
    .string()
    .email('wrongEmailFormat')
    .when('emailConsent', {
      is: (value) => value === true || value === 'true',
      then: () => yup.string().email('wrongEmailFormat').required('email'),
      otherwise: () => yup.string().notRequired(),
    }),
});

export const schoolRegistrationFormSchema = yup.object().shape({
  establishmentDetails: schoolFormEstablishmentDetailsSchema,
  inchargePersonDetails: inchargePersonSchema,
  inchargePersonAnother: yup.boolean().required('inchargePersonAnother'),
  inchargePersonAnotherDetails: yup.object().when('inchargePersonAnother', {
    is: (value) => value === 'false' || value === false,
    then: () => inchargePersonSchema,
    otherwise: () => yup.object().nullable(),
  }),
  classes: yup.array().of(schoolRegistrationFormClassSchema),
  emailConsent: emailConsentSchema,
});

const otherGroupsFormEstablishmentDetailsSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([...otherGroupsFormEstablishmentTypes.map((type) => type.value)])
    .required('establishmentType'),
  postalCode: yup.string().required('postalCode'),
  name: yup.string().required('name'),
  city: yup.string().required('city'),
  address: yup.string().required('address'),
  department: yup.string().required('department'),
});

const otherGroupsRegistrationFormGroupSchema = yup.object().shape({
  name: yup.string().required('name'),
  numberOfStudents: yup
    .number()
    .min(1, 'numberOfStudents')
    .max(50, 'numberOfStudents')
    .required('numberOfStudents'),
  visitDay: yup
    .string()
    .oneOf(visitDays.map((day) => day.value))
    .required('visitDay'),
  arrivalTime: yup
    .string()
    .test('visitDay', 'arrivalTime', (value, testContext) => {
      const { parent } = testContext;
      const { visitDay } = parent;
      return arrivalTimes[visitDay].map((time) => time.value).includes(value);
    })
    .required('arrivalTime'),
  departureTime: yup
    .string()
    .test('visitDay', 'departureTime', (value, testContext) => {
      const { parent } = testContext;
      const { visitDay } = parent;
      return departureTimes[visitDay].map((time) => time.value).includes(value);
    })
    .required('departureTime'),
  modeOfTransport: yup
    .string()
    .oneOf(modesOfTransport.map((transport) => transport.value))
    .required('modeOfTransport'),
  otherModeOfTransport: yup.string().when('modeOfTransport', {
    is: (modeOfTransport) => modeOfTransport === 'other',
    then: () => yup.string().required('otherModeOfTransport'),
    otherwise: () => yup.string().notRequired(),
  }),
  picnicOnSite: yup.boolean().required('picnicOnSite'),
  handicap: yup.boolean().required('handicap'),
  handicapDescription: yup.string().when('handicap', {
    is: (value) => value === true || value === 'true',
    then: () => yup.string().required('handicapDescription'),
    otherwise: () => yup.string().notRequired(),
  }),
  responsibleInformation: yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      function: yup.string(),
      email: yup.string().email('wrongEmailFormat'),
      phone: yup.string(),
    })
    .nullable(),
});

const otherGroupsRegistrationFormClassSchema = yup.object().shape({
  name: yup.string().required('name'),
  level: yup.string().required('level'),
  numberOfStudents: yup
    .number()
    .min(1, 'numberOfStudents')
    .max(50, 'numberOfStudents')
    .required('numberOfStudents'),
  sector: yup.string(),
  visitDay: yup
    .string()
    .oneOf(visitDays.map((day) => day.value))
    .required('visitDay'),
  arrivalTime: yup
    .string()
    .test('visitDay', 'arrivalTime', (value, testContext) => {
      const { parent } = testContext;
      const { visitDay } = parent;
      return arrivalTimes[visitDay].map((time) => time.value).includes(value);
    })
    .required('arrivalTime'),
  departureTime: yup
    .string()
    .test('visitDay', 'departureTime', (value, testContext) => {
      const { parent } = testContext;
      const { visitDay } = parent;
      return departureTimes[visitDay].map((time) => time.value).includes(value);
    })
    .required('departureTime'),
  modeOfTransport: yup
    .string()
    .oneOf(modesOfTransport.map((transport) => transport.value))
    .required('modeOfTransport'),
  otherModeOfTransport: yup.string().when('modeOfTransport', {
    is: (modeOfTransport) => modeOfTransport === 'other',
    then: () => yup.string().required('otherModeOfTransport'),
    otherwise: () => yup.string().notRequired(),
  }),
  picnicOnSite: yup.boolean().required('picnicOnSite'),
  handicap: yup.boolean().required('handicap'),
  handicapDescription: yup.string().when('handicap', {
    is: (value) => value === true || value === 'true',
    then: () => yup.string().required('handicapDescription'),
    otherwise: () => yup.string().notRequired(),
  }),
  responsibleInformation: yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      function: yup.string(),
      email: yup.string().email('wrongEmailFormat'),
      phone: yup.string(),
    })
    .nullable(),
});

export const otherGroupsRegistrationFormSchema = yup.object().shape({
  establishmentDetails: otherGroupsFormEstablishmentDetailsSchema,
  inchargePersonDetails: inchargePersonSchema,
  classes: yup.array().when('establishmentDetails.type', {
    is: (type) =>
      [...otherGroupsFormEstablishmentTypes]
        .slice(0, 2)
        .map((type) => type.value)
        .includes(type),
    then: () => yup.array().of(otherGroupsRegistrationFormClassSchema),
    otherwise: () => yup.array().of(otherGroupsRegistrationFormGroupSchema),
  }),
  emailConsent: emailConsentSchema,
});
